import React, { useState } from 'react';
import { FileText, Download, Eye, FolderTree, ChevronRight, ChevronDown } from 'lucide-react';

const PIDsEquipmentHierarchy = () => {
  const [expandedNodes, setExpandedNodes] = useState(['area-1']);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const equipmentHierarchy = [
    {
      id: 'area-1',
      name: 'Distillation Area',
      type: 'area',
      children: [
        {
          id: 'unit-1',
          name: 'Distillation Column T-101',
          type: 'equipment',
          tag: 'T-101',
          pidReference: 'PID-001-2024',
          specs: { height: '25m', diameter: '2.5m', pressure: '5 bar' }
        },
        {
          id: 'unit-2',
          name: 'Reboiler E-101',
          type: 'equipment',
          tag: 'E-101',
          pidReference: 'PID-001-2024',
          specs: { duty: '2.5 MW', area: '150 m²', pressure: '6 bar' }
        }
      ]
    },
    {
      id: 'area-2',
      name: 'Reaction Area',
      type: 'area',
      children: [
        {
          id: 'unit-3',
          name: 'CSTR Reactor R-201',
          type: 'equipment',
          tag: 'R-201',
          pidReference: 'PID-002-2024',
          specs: { volume: '50 m³', temperature: '180°C', pressure: '15 bar' }
        },
        {
          id: 'unit-4',
          name: 'Feed Pump P-201',
          type: 'equipment',
          tag: 'P-201',
          pidReference: 'PID-002-2024',
          specs: { flow: '100 m³/h', head: '50m', power: '45 kW' }
        }
      ]
    }
  ];

  const pids = [
    {
      id: 1,
      name: 'Distillation Unit P&ID',
      documentNumber: 'PID-001-2024',
      revision: 'Rev. 5',
      lastUpdated: '2025-11-15',
      status: 'Approved',
      equipmentCount: 12
    },
    {
      id: 2,
      name: 'Reaction Section P&ID',
      documentNumber: 'PID-002-2024',
      revision: 'Rev. 3',
      lastUpdated: '2025-10-20',
      status: 'Approved',
      equipmentCount: 8
    },
    {
      id: 3,
      name: 'Utilities P&ID',
      documentNumber: 'PID-003-2024',
      revision: 'Rev. 2',
      lastUpdated: '2025-12-01',
      status: 'Under Review',
      equipmentCount: 15
    }
  ];

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev =>
      prev?.includes(nodeId)
        ? prev?.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const renderHierarchyNode = (node, level = 0) => {
    const isExpanded = expandedNodes?.includes(node?.id);
    const hasChildren = node?.children && node?.children?.length > 0;

    return (
      <div key={node?.id}>
        <div
          className={`flex items-center space-x-2 p-3 rounded-lg cursor-pointer transition-colors ${
            selectedEquipment === node?.id ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50'
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
          onClick={() => {
            if (hasChildren) toggleNode(node?.id);
            if (node?.type === 'equipment') setSelectedEquipment(node?.id);
          }}
        >
          {hasChildren && (
            <button className="p-1">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-6" />}
          
          <FolderTree className={`w-5 h-5 ${node?.type === 'area' ? 'text-blue-600' : 'text-gray-600'}`} />
          
          <div className="flex-1">
            <p className="font-medium text-gray-900">{node?.name}</p>
            {node?.tag && <p className="text-xs text-gray-600">Tag: {node?.tag}</p>}
          </div>

          {node?.type === 'equipment' && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
              {node?.pidReference}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node?.children?.map(child => renderHierarchyNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const selectedEquipmentData = equipmentHierarchy?.flatMap(area => area?.children)?.find(eq => eq?.id === selectedEquipment);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Equipment Hierarchy */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Equipment Hierarchy</h3>
          <p className="text-sm text-gray-600 mt-1">Browse by area and equipment</p>
        </div>
        <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
          {equipmentHierarchy?.map(node => renderHierarchyNode(node))}
        </div>
      </div>
      {/* P&IDs and Equipment Details */}
      <div className="lg:col-span-2 space-y-6">
        {/* P&IDs List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">P&ID Documents</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {pids?.map((pid) => (
              <div key={pid?.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">{pid?.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        pid?.status === 'Approved' ?'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                      }`}>
                        {pid?.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Document:</span>
                        <span className="ml-2 font-medium text-gray-900">{pid?.documentNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Revision:</span>
                        <span className="ml-2 font-medium text-gray-900">{pid?.revision}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Equipment Count:</span>
                        <span className="ml-2 font-medium text-gray-900">{pid?.equipmentCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Updated:</span>
                        <span className="ml-2 font-medium text-gray-900">{pid?.lastUpdated}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                      <Eye className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Equipment Details */}
        {selectedEquipmentData && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Equipment Details</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-xl font-bold text-gray-900 mb-1">{selectedEquipmentData?.name}</h4>
                <p className="text-sm text-gray-600">Tag: {selectedEquipmentData?.tag}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {Object.entries(selectedEquipmentData?.specs)?.map(([key, value]) => (
                  <div key={key} className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 uppercase mb-1">{key}</p>
                    <p className="text-lg font-semibold text-gray-900">{value}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center space-x-3">
                <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2">
                  <Eye className="w-4 h-4" />
                  <span>View P&ID</span>
                </button>
                <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center space-x-2">
                  <Download className="w-4 h-4" />
                  <span>Download Datasheet</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PIDsEquipmentHierarchy;